import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import { IAccountDetailsDTO } from '../../../api/accounts.dao';
import { getShortTextLengthError } from '../../../infrastructure/utils/form-validation.utils';

class AccountFormDataVM {
    @mobx.observable
    private name: string;

    @mobx.observable
    private salesforceAccountId: string = '';

    @mobx.observable
    private allowedProviders: string = '';

    private accountId: number;

    @mobx.observable
    private isFormChecked: boolean;

    @mobx.observable
    private serverErrorMessage: string;

    @mobx.observable
    public validationError: string[];

    constructor(
        accountDetailsDTO: IAccountDetailsDTO,
    ) {
        this.name = accountDetailsDTO.name;
        this.accountId = accountDetailsDTO.accountId;
        this.salesforceAccountId = accountDetailsDTO.salesforceAccountId || '';
        this.allowedProviders = accountDetailsDTO.allowedProviders ? accountDetailsDTO.allowedProviders.join('\n') : '';
    }

    public getSalesforceAccountID(): string {
        return this.salesforceAccountId;
    }

    public getAllowedProviders(): string {
        return this.allowedProviders;
    }

    public getName(): string {
        return this.name;
    }

    public getServerErrorMessage(): string {
        return this.serverErrorMessage;
    }

    @mobx.action
    public setServerErrorMessage(message: string): void {
        this.serverErrorMessage = message;
    }

    @mobx.action
    public setName = (name: string) => {
        this.setServerErrorMessage('');
        this.name = name;
    };

    @mobx.action
    public setSalesforceAccountID = (salesforceAccountId: string) => {
        this.setServerErrorMessage('');
        this.salesforceAccountId = salesforceAccountId;
    };

    @mobx.action
    public setAllowedProviders = (allowedProviders: string) => {
        this.setServerErrorMessage('');
        this.allowedProviders = allowedProviders;
    };

    public getNameError(intl: IntlShape): string | null {
        return this.name
            ? getShortTextLengthError(
                intl.formatMessage(
                    {
                        id: 'oe.accounts.accountName',
                        description: 'Account name title',
                        defaultMessage: 'Account name',
                    }),
                this.name
            )
            : intl.formatMessage({
                id: `oe.accounts.accountCannotBeBlank`,
                defaultMessage: 'Account name cannot be blank',
                description: 'Error message in account popover'
            });
    }

    public getSalesforceAccountIDError(intl: IntlShape): string | null {
        return getShortTextLengthError(
            intl.formatMessage(
                {
                    id: 'oe.common.salesforceAccountId',
                    description: 'salesforceAccountId Name title',
                    defaultMessage: 'Salesforce Account ID',
                }),
                this.salesforceAccountId,
            );
    }

    public getAllowedProvidersError(intl: IntlShape): string | null {
        return this.allowedProviders.split('\n')
            .map((el: string) => el.trim()).filter(el => el.length).length > 10 ?
            intl.formatMessage(
                {
                    id: 'oe.common.allowedProvidersError',
                    description: 'allowedProviders error message',
                    defaultMessage: 'Allowed Providers count exceeded maximum of 10 items',
                }) : null;
    }

    public isFormValid(intl: IntlShape): boolean {
        return !this.getNameError(intl)
            && !this.getSalesforceAccountIDError(intl)
            && !this.getAllowedProvidersError(intl);
    }

    @mobx.action
    public allowToShowValidationErrors(): void {
        this.isFormChecked = true;
    }

    public showValidationErrors(): boolean {
        return this.isFormChecked;
    }

    public toApiAccountDTO(): IAccountDetailsDTO {
        return {
            name: this.name?.trim(),
            accountId: this.accountId,
            salesforceAccountId: this.salesforceAccountId?.trim() || null,
            allowedProviders: this.allowedProviders.split('\n')
                .map((el: string) => el.trim()).filter(el => el.length)
        };
    }
}

export { AccountFormDataVM };